import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Field';
import { Banner } from '@/components/ui/Feedback';
import type { Indicator, IndicatorInput } from './api';

const SCORES = [1, 2, 3, 4] as const;

interface Props {
  /** null = menambah indikator baru. */
  indicator: Indicator | null;
  scaleLabels: { score: number; label: string }[];
  evidenceLabel: string;
  nextNumber: number;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (input: IndicatorInput) => void;
}

/**
 * Penyunting satu indikator beserta empat kriteria dan daftar MoV-nya.
 *
 * Semuanya satu formulir, bukan beberapa langkah terpisah: sebuah indikator
 * tanpa kriteria lengkap tidak bisa dipakai menilai apa pun, jadi tidak ada
 * gunanya membolehkan tersimpan separuh.
 */
export function IndicatorEditor({
  indicator,
  scaleLabels,
  evidenceLabel,
  nextNumber,
  saving,
  error,
  onCancel,
  onSave,
}: Props) {
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [weight, setWeight] = useState('');
  const [priorityYear, setPriorityYear] = useState('');
  const [criteria, setCriteria] = useState<Record<number, string>>({});
  const [movText, setMovText] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setNumber(String(indicator?.number ?? nextNumber));
    setName(indicator?.name ?? '');
    setDescription(indicator?.description ?? '');
    setWeight(indicator ? String(indicator.weight) : '');
    setPriorityYear(indicator?.priorityYear ? String(indicator.priorityYear) : '');
    setCriteria(
      Object.fromEntries(SCORES.map((s) => [s, indicator?.criteria.find((c) => c.score === s)?.description ?? ''])),
    );
    setMovText((indicator?.mov ?? []).map((m) => m.label).join('\n'));
    setFieldErrors({});
  }, [indicator, nextNumber]);

  function submit(e: React.FormEvent) {
    e.preventDefault();

    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'Nama indikator wajib diisi';
    if (!number.trim() || Number(number) < 1) errors.number = 'Nomor wajib diisi';
    const w = Number(weight);
    if (weight.trim() === '' || Number.isNaN(w)) errors.weight = 'Bobot wajib diisi';
    else if (w < 0 || w > 100) errors.weight = 'Bobot antara 0 dan 100';

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    onSave({
      number: Number(number),
      name: name.trim(),
      description: description.trim() || null,
      weight: w,
      priorityYear: priorityYear ? Number(priorityYear) : null,
      criteria: SCORES.map((score) => ({ score, description: criteria[score] ?? '' })),
      // Satu MoV per baris — cara paling dekat dengan kebiasaan menyalin dari
      // sel Excel, tanpa memaksa menambah baris satu per satu.
      mov: movText
        .split('\n')
        .map((label) => label.trim())
        .filter(Boolean)
        .map((label) => ({ label, requirement: 'optional' as const })),
    });
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:p-4"
    >
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col rounded-t-[var(--radius-card)] bg-surface shadow-[var(--shadow-hover)] sm:rounded-[var(--radius-card)]">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-[19px] font-semibold text-ink">
            {indicator ? `Ubah Indikator ${indicator.number}` : 'Indikator Baru'}
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {error && (
            <div className="mb-4">
              <Banner tone="danger">{error}</Banner>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-4">
            <Input
              label="No."
              type="number"
              inputMode="numeric"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              error={fieldErrors.number}
              required
            />
            <div className="sm:col-span-3">
              <Input
                label="Nama indikator"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={fieldErrors.name}
                required
              />
            </div>
          </div>

          <div className="mt-4">
            <Textarea
              label="Deskripsi"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              hint="Penjelasan apa yang sebenarnya diukur indikator ini."
            />
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Input
              label="Bobot (%)"
              type="number"
              inputMode="decimal"
              step="0.5"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              error={fieldErrors.weight}
              hint="Bobot terhadap indeks 0–100, bukan terhadap dimensinya."
              required
            />
            <Select
              label="Indikator prioritas"
              value={priorityYear}
              onChange={(e) => setPriorityYear(e.target.value)}
              hint="Kosongkan bila bukan indikator prioritas."
            >
              <option value="">— bukan prioritas —</option>
              <option value="1">Tahun Program 1</option>
              <option value="2">Tahun Program 2</option>
              <option value="3">Tahun Program 3</option>
            </Select>
          </div>

          <h3 className="mt-6 mb-1 text-[17px] font-semibold text-ink">Kriteria nilai</h3>
          <p className="mb-3 text-[13px] text-ink-secondary">
            Kalimat yang membedakan satu tingkat dari tingkat berikutnya. Inilah yang dibaca
            assessor saat memilih angka, jadi bedanya harus bisa diamati — bukan selera.
          </p>

          <div className="flex flex-col gap-3">
            {SCORES.map((score) => (
              <Textarea
                key={score}
                label={`Nilai ${score}${scaleLabels.find((s) => s.score === score)?.label ? ` — ${scaleLabels.find((s) => s.score === score)!.label}` : ''}`}
                value={criteria[score] ?? ''}
                onChange={(e) => setCriteria((c) => ({ ...c, [score]: e.target.value }))}
              />
            ))}
          </div>

          <div className="mt-6">
            <Textarea
              label={evidenceLabel}
              value={movText}
              onChange={(e) => setMovText(e.target.value)}
              hint="Satu bukti per baris."
            />
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2.5 border-t border-border px-5 py-4 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            Batal
          </Button>
          <Button type="submit" loading={saving}>
            Simpan Indikator
          </Button>
        </div>
      </div>
    </form>
  );
}
